import React, { useEffect, useState } from "react";
import { NavLink, Navigate, Outlet, useNavigate, Link } from "react-router-dom";
import { FileText, Award, Inbox, MessageSquare, Settings, LogOut, ExternalLink } from "lucide-react";
import Logo from "../../components/Logo";
import { Seo } from "../../components/Seo";
import { api, getToken, setToken } from "../../api";

const NAV = [
  { to: "/admin/blog", label: "Blog posts", icon: FileText, testid: "admin-nav-blog" },
  { to: "/admin/case-studies", label: "Case studies", icon: Award, testid: "admin-nav-case-studies" },
  { to: "/admin/leads", label: "Leads", icon: Inbox, testid: "admin-nav-leads" },
  { to: "/admin/conversations", label: "Conversations", icon: MessageSquare, testid: "admin-nav-conversations" },
  { to: "/admin/settings", label: "Settings", icon: Settings, testid: "admin-nav-settings" },
];

export default function AdminLayout() {
  const nav = useNavigate();
  const [user, setUser] = useState(null);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    if (!getToken()) return;
    api.get("/auth/me").then((r) => setUser(r.data)).catch(() => setUser(false));
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
  }, []);

  if (!getToken() || user === false) return <Navigate to="/admin/login" replace />;

  const logout = () => {
    setToken(null);
    nav("/admin/login", { replace: true });
  };

  return (
    <div className="min-h-screen bg-[var(--fr-bg)] lg:grid lg:grid-cols-[250px_1fr]" data-testid="admin-layout">
      <Seo title="Admin" noindex />
      <aside className="relative bg-white border-b lg:border-b-0 lg:border-r border-[var(--fr-border)] flex flex-col lg:min-h-screen lg:sticky lg:top-0 lg:h-screen">
        <div className="px-6 pr-24 lg:pr-6 h-[64px] flex items-center justify-between border-b border-[var(--fr-border)]">
          <Logo height={22} testid="admin-logo" />
          <span className="chip !text-[10px] uppercase tracking-wide">Admin</span>
        </div>
        <nav className="p-3 flex lg:flex-col gap-1 overflow-x-auto">
          {NAV.map(({ to, label, icon: Icon, testid }) => (
            <NavLink
              key={to}
              to={to}
              data-testid={testid}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 h-10 rounded-xl text-sm font-medium whitespace-nowrap transition-colors ${
                  isActive ? "bg-[var(--fr-primary-tint)] text-[var(--fr-primary-dark)]" : "text-[var(--fr-ink-2)] hover:text-[var(--fr-ink)] hover:bg-[var(--fr-bg-alt)]"
                }`
              }
            >
              <Icon size={16} /> {label}
              {label === "Leads" && (stats?.newLeads + stats?.newPartners) > 0 && (
                <span className="ml-auto text-[11px] font-bold bg-[var(--fr-primary)] text-white rounded-full px-2 py-0.5" data-testid="admin-new-leads-badge">
                  {stats.newLeads + stats.newPartners}
                </span>
              )}
            </NavLink>
          ))}
        </nav>
        <div className="mt-auto p-3 border-t border-[var(--fr-border)] hidden lg:block">
          <Link to="/blog" target="_blank" className="flex items-center gap-3 px-3 h-10 rounded-xl text-sm text-[var(--fr-ink-2)] hover:text-[var(--fr-ink)] hover:bg-[var(--fr-bg-alt)]">
            <ExternalLink size={16} /> View site
          </Link>
          <div className="px-3 py-3 text-xs text-[var(--fr-muted)] truncate" data-testid="admin-user-email">{user?.email || "…"}</div>
          <button type="button" onClick={logout} className="flex w-full items-center gap-3 px-3 h-10 rounded-xl text-sm text-[var(--fr-ink-2)] hover:text-[var(--fr-ink)] hover:bg-[var(--fr-bg-alt)]" data-testid="admin-logout">
            <LogOut size={16} /> Sign out
          </button>
        </div>
        <button type="button" onClick={logout} className="lg:hidden absolute right-5 top-[22px] text-xs text-[var(--fr-muted)] underline" data-testid="admin-logout-mobile">
          Sign out
        </button>
      </aside>
      <main className="min-w-0 px-5 md:px-10 py-8 md:py-10">
        <Outlet />
      </main>
    </div>
  );
}
